import Currency from "@/components/Currency";
import OrderConfirm from "@/components/OrderConfirm";
import CreditCardView from "@/components/Payments/CreditCardView";
import SubscriptionCard from "@/components/SubscriptionCard";
import VideoActions from "@/components/VideoPost/VideoActions";
import VideoOverlay from "@/components/VideoPost/VideoOverlay";
import AuthContext from "@/context/auth";
import apiClient from "@/libs/api";
import useAPI from "@/libs/hooks/api";
import useDeviceInfo from "@/libs/hooks/device-info";
import { useIsCreator } from "@/libs/hooks/useIsCreator";
import { creatorStore } from "@/libs/stores/creator.store";
import exploreStore from "@/libs/stores/explore.store";
import { fullscreenStore } from "@/libs/stores/fullscreen.store";
import { playerStore } from "@/libs/stores/player.store";
import VideoThumbnail from "@/libs/video/components/VideoThumbnail";
import ShortsPlayer from "@/libs/video/shaka-player";
import { Box, Button, Modal, ModalBody, ModalContent, ModalOverlay, useDisclosure, useToast } from "@chakra-ui/react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MobileNavigation from "../MobileNavigation";

const VideoPost = ({ id, store = playerStore }) => {
  const [video, setVideo] = useState(() => store.getVideo(id)?.video);
  const { t } = useTranslation();
  const toast = useToast();
  const { width, height, isMobile } = useDeviceInfo();
  const api = useAPI();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const price = video?.price || 0;
  
  // Subscription state
  const [subscriptionRequiredModalOpen, setSubscriptionRequiredModalOpen] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [ecpayToken, setEcpayToken] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const ecpayInitializedRef = useRef(false);
  const { data: creatorData } = useIsCreator();
  const isCreator = creatorData?.isCreator;
  
  // Get creator info from store
  const creator = useMemo(() => {
    const creatorId = video?.creatorId;
    return creatorId ? creatorStore.getCreator(creatorId) : null;
  }, [video?.creatorId]);

  const cta = useMemo(() => {
    if (user)
      return t("videos.actions.unlock_video_with", {
        price,
        currency: t("common.currency"),
      });
    return t("common.cta.register_to_unlock");
  }, [price, t, user]);

  const onCTA = useCallback(async () => {
    console.log("onCTA");
    if (user) return onOpen();
    navigate("/auth/register");
  }, [user, onOpen, navigate, toast]);

  // Fetch subscription plan
  const fetchSubscriptionPlan = useCallback(async (creatorId) => {
    if (!creatorId) return;

    try {
      setIsCheckingSubscription(true);
      
      // Fetch subscription plan (public endpoint)
      const plan = await apiClient.getCreatorSubscriptionPlan(creatorId);
      setSubscriptionPlan(plan);

      // Check subscription status if user is logged in
      if (user) {
        try {
          const status = await apiClient.checkCreatorSubscription(creatorId);
          setIsSubscribed(status?.subscribed || false);
        } catch (error) {
          // If 401 or 404, user is not subscribed
          const status = error && typeof error === 'object' && 'status' in error ? error.status : null;
          if (status === 401 || status === 404) {
            setIsSubscribed(false);
          } else {
            console.error("Failed to check subscription status:", error);
            setIsSubscribed(false);
          }
        }
      } else {
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error("Failed to fetch subscription plan:", error);
      setSubscriptionPlan(null);
    } finally {
      setIsCheckingSubscription(false);
    }
  }, [user]);

  // Subscription handlers
  const handleCancelSubscription = useCallback(() => {
    setSubscriptionRequiredModalOpen(false);
    setSubscriptionDetails(null);
    setEcpayToken(null);
    ecpayInitializedRef.current = false;
    
    // Clean up ECPay payment div content
    const ecpayDiv = document.getElementById('ECPayPayment');
    if (ecpayDiv) {
      ecpayDiv.innerHTML = '';
    }
  }, []);

  const handleSubscribe = useCallback(async () => {
    const creatorId = video?.creatorId;
    if (!creatorId || !user) {
      toast({
        title: t("errors.login_required") || "Please login to subscribe",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Check if user is a creator - creators cannot subscribe
    if (isCreator) {
      toast({
        title: "Creators cannot subscribe to other creators",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      // Step 1: Get payment token
      const creatorSlug = video?.creator?.slug || '';
      const { tradeId, token } = await apiClient.getSubscriptionPaymentToken(creatorId, `/videos/${id}`);
      
      // Step 2: Wait for ECPay to be loaded
      let waited = 0;
      const waitPeriod = 100;
      while (!window.ECPayLoaded && waited < 10000) {
        await new Promise((resolve) => setTimeout(resolve, waitPeriod));
        waited += waitPeriod;
      }

      if (!window.ECPayLoaded) {
        throw new Error("ECPay SDK failed to load");
      }

      // Step 3: Set subscription details and token
      setSubscriptionDetails({
        type: "creator_subscription",
        creatorId: creatorId,
        tradeId,
        ecpayToken: token,
        cost: subscriptionPlan?.price || 0,
        entry: t("payment.creator_subscription.purchase", { creator: creatorSlug }),
      });
      setEcpayToken(token);
    } catch (error) {
      console.error("Subscribe error:", error);
      toast({
        title: error.body?.error || error.message || "Failed to start subscription",
        status: "error",
        duration: 3000,
      });
    }
  }, [video?.creatorId, video?.creator?.slug, user, isCreator, toast, t, subscriptionPlan, id]);

  // Initialize ECPay only when subscription modal is open
  useEffect(() => {
    if (!subscriptionRequiredModalOpen) {
      // Reset when modal closes
      ecpayInitializedRef.current = false;
      return;
    }

    // Only initialize ECPay if payment provider is ECPay
    if (import.meta.env.VITE_PAYMENT_SERVICE_PROVIDER !== "ecpay") {
      return;
    }

    // If ECPay is already loaded and initialized, don't reset it
    if (window.ECPay && window.ECPayLoaded) {
      return;
    }

    // Initialize ECPay SDK when modal opens
    window.ECPayLoaded = false;
    
    function loadJQuery() {
      return new Promise((resolve) => {
        if (window.jQuery) {
          resolve();
        } else {
          const script = document.createElement("script");
          script.src = "https://code.jquery.com/jquery-3.5.1.min.js";
          script.onload = resolve;
          document.body.appendChild(script);
        }
      });
    }

    function loadECPaySDK() {
      return new Promise((resolve) => {
        if (window.ECPay) {
          resolve();
        } else {
          const script = document.createElement("script");
          script.id = "ecpay-sdk-script";
          script.src =
            import.meta.env.VITE_TAPPAY_ENV === "production"
              ? "https://ecpg.ecpay.com.tw/Scripts/sdk-1.0.0.js?t=20210121100116"
              : "https://ecpg-stage.ecpay.com.tw/Scripts/sdk-1.0.0.js?t=20210121100116";
          script.onload = resolve;
          document.body.appendChild(script);
        }
      });
    }

    function initializeECPay() {
      const isProd = import.meta.env.VITE_TAPPAY_ENV === "production";
      window.ECPay.initialize(
        isProd ? "Prod" : "Stage",
        1,
        function callBack(errMsg) {
          if (errMsg !== null) {
            console.log("ECPay 初始化失敗:", errMsg);
            window.ECPayLoaded = false;
          } else {
            console.log("ECPay 初始化成功");
            window.ECPayLoaded = true;
          }
        },
      );
    }

    loadJQuery().then(loadECPaySDK).then(initializeECPay);
  }, [subscriptionRequiredModalOpen]);

  // Initialize ECPay payment UI when modal opens and token is ready
  useEffect(() => {
    if (!subscriptionRequiredModalOpen || !ecpayToken || !subscriptionDetails || ecpayInitializedRef.current) return;

    // Wait for DOM to be ready
    const initECPay = () => {
      const ecpayDiv = document.getElementById('ECPayPayment');
      if (!ecpayDiv) {
        // Retry after a short delay if div doesn't exist yet
        setTimeout(initECPay, 50);
        return;
      }

      // Clean up any existing content first
      ecpayDiv.innerHTML = '';

      // Mark as initialized to prevent multiple calls
      ecpayInitializedRef.current = true;

      const cb = function (errMsg) {
        if (errMsg) {
          toast({
            title: errMsg || "Failed to initialize payment",
            status: "error",
            duration: 3000,
          });
          handleCancelSubscription();
        }
      };

      try {
        window.ECPay.createPayment(ecpayToken, window.ECPay.Language.zhTW, cb, 'V2');
      } catch (e) {
        console.error("ECPay initialization error:", e);
        toast({
          title: "Failed to initialize payment",
          status: "error",
          duration: 3000,
        });
        handleCancelSubscription();
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(initECPay);
    });
  }, [subscriptionRequiredModalOpen, ecpayToken, subscriptionDetails, toast, handleCancelSubscription]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      ecpayInitializedRef.current = false;
      const ecpayDiv = document.getElementById('ECPayPayment');
      if (ecpayDiv) {
        ecpayDiv.innerHTML = '';
      }
    };
  }, []);

  // 解鎖視頻功能
  const unlockVideo = useCallback(async () => {
    await api.unlockVideo(id);
    const videoData = await api.getVideo(id);
    console.log("unlockVideo", videoData);
    const v = store.getVideo(id);
    v.video = videoData;
    await v.clearCache();
    await v.load();
    exploreStore.fetchFreshData();
    onClose();

    runInAction(() => {
      setVideo(videoData);
    });

    // Show subscription prompt after successful purchase
    const creatorId = videoData?.creatorId;
    if (creatorId && user && !isCreator) {
      // Fetch subscription plan and check subscription status
      try {
        setIsCheckingSubscription(true);
        const plan = await apiClient.getCreatorSubscriptionPlan(creatorId);
        
        if (plan) {
          // Check if user is subscribed
          let subscribed = false;
          try {
            const status = await apiClient.checkCreatorSubscription(creatorId);
            subscribed = status?.subscribed || false;
          } catch (error) {
            const status = error && typeof error === 'object' && 'status' in error ? error.status : null;
            if (status !== 401 && status !== 404) {
              console.error("Failed to check subscription status:", error);
            }
          }

          // Show modal if plan exists and user is not subscribed
          if (!subscribed) {
            setSubscriptionPlan(plan);
            setIsSubscribed(false);
            setSubscriptionRequiredModalOpen(true);
          } else {
            setIsSubscribed(true);
          }
        }
      } catch (error) {
        console.error("Failed to fetch subscription plan:", error);
      } finally {
        setIsCheckingSubscription(false);
      }
    }
  }, [api, id, video, user, isCreator]);

  const handleContextMenu = (event) => {
    event.preventDefault();
    console.log("handleContextMenu");

    navigator.clipboard.writeText(id).then(() => {
      toast({
        title: "ID 已複製到剪貼簿！",
        status: "success",
        duration: 2000,
      });
    });
  };

  return (
    <Box
      position="relative"
      height="100%"
      maxW={{ base: "none", md: ((height * 9) / 16) * 0.85 }}
      mx="auto"
      py={{ base: 0, md: 5 }}
      onContextMenu={handleContextMenu} // 右鍵事件
    >
      <Box
        position="relative"
        height="100%"
        aspectRatio={{
          base:
            width /
            (height - fullscreenStore.isFullscreen
              ? 0
              : MobileNavigation.HEIGHT),
          md: 9 / 16,
        }}
        width="100%"
      >
        <Box
          width="100%"
          height="100%"
          overflow="hidden"
          position="relative"
          className="video-container"
          zIndex={2}
        >
          <Box position="absolute" height="100%" width="100%">
            <ShortsPlayer videoId={id} store={store} />
          </Box>
        </Box>
        {<VideoThumbnail src={video?.thumbnails} zIndex={1} />}
        {store.getActivePlayer()?.id === id && !store.isBuffering && (
          <VideoOverlay store={store} zIndex={3}></VideoOverlay>
        )}
        <Box
          position="absolute"
          bottom={0}
          color="white"
          width="100%"
          transition="opacity 0.3s"
          zIndex={4}
        >
          <Box p={5}>
            <VideoActions id={id} store={store} />
            {!video?.isUnlocked && video?.price && (
              <Button
                mt={4}
                width="full"
                variant="themed-gradient"
                onClick={onCTA}
              >
                <Currency size={6} />
                {cta}
                <Currency size={6} />
              </Button>
            )}
          </Box>
        </Box>
        <OrderConfirm
          isOpen={isOpen}
          onClose={onClose}
          onCancel={onClose}
          onConfirm={unlockVideo}
          entry={t("videos.actions.unlock_video")}
          amount={-price}
        />
        
        {/* Subscription Modal - Show after video purchase */}
        <Modal
          isOpen={subscriptionRequiredModalOpen}
          onClose={handleCancelSubscription}
          isCentered
          size="xl"
          closeOnOverlayClick={!subscriptionDetails}
        >
          <ModalOverlay bg="rgba(0,0,0,.8)" />
          <ModalBody>
            <ModalContent bg="primary.100" color="white" borderRadius={12} border="1px solid" borderColor="secondary.500" p={6}>
              {!subscriptionDetails ? (
                // Show subscription info and button to start subscription
                <SubscriptionCard
                  subscriptionPlan={subscriptionPlan}
                  isLoading={isCheckingSubscription}
                  isDisabled={isCheckingSubscription || isCreator || !subscriptionPlan || !user}
                  onClick={handleSubscribe}
                  variant="modal"
                  title={t("payment.creator_subscription.purchase", { creator: creator?.displayName || video?.creator?.slug || "" })}
                  description={creator?.displayName ? `訂閱 ${creator.displayName} 享受更多福利` : "訂閱創作者享受更多福利"}
                  showLoginPrompt={!user}
                />
              ) : (
                // Show payment form
                <CreditCardView
                  active={true}
                  details={subscriptionDetails}
                  onCancel={handleCancelSubscription}
                />
              )}
            </ModalContent>
          </ModalBody>
        </Modal>
      </Box>
    </Box>
  );
};

export default observer(VideoPost);
